import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Loader2 } from 'lucide-react';
import { listAvatars, listVoices, HeygenAvatar, HeygenVoice } from '@/services/agents/vgenAgentService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faClosedCaptioning, faImage } from '@fortawesome/free-solid-svg-icons';

interface VGenSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (avatar: HeygenAvatar | null, voice: HeygenVoice | null, options?: { captions_enabled?: boolean; captions_text?: string; background_image_url?: string }) => void;
  isDarkMode: boolean;
}

export const VGenSelectionModal = ({ isOpen, onClose, onSelect, isDarkMode }: VGenSelectionModalProps) => {
  const [avatars, setAvatars] = useState<HeygenAvatar[]>([]);
  const [voices, setVoices] = useState<HeygenVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState<HeygenAvatar | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<HeygenVoice | null>(null);
  const [avatarSearch, setAvatarSearch] = useState('');
  const [voiceSearch, setVoiceSearch] = useState('');
  const [debouncedAvatarSearch, setDebouncedAvatarSearch] = useState('');
  const [debouncedVoiceSearch, setDebouncedVoiceSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'avatars' | 'voices' | 'advanced'>('avatars');
  // New: options
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [isUploadingBg, setIsUploadingBg] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Voice preview control
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  // Debounce search inputs to avoid filtering on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAvatarSearch(avatarSearch), 200);
    return () => clearTimeout(t);
  }, [avatarSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedVoiceSearch(voiceSearch), 200);
    return () => clearTimeout(t);
  }, [voiceSearch]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Avoid refetching if already loaded once (modal reopen)
      if (avatars.length && voices.length) {
        return;
      }
      const [avatarsList, voicesList] = await Promise.all([
        listAvatars(),
        listVoices(),
      ]);
      setAvatars(avatarsList);
      setVoices(voicesList);
    } catch (error) {
      console.error('Failed to load HeyGen data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePickBackground = () => {
    fileInputRef.current?.click();
  };

  const handleBackgroundFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsUploadingBg(true);

      const CLOUDINARY_CLOUD_NAME = (import.meta as any).env?.VITE_CLOUDINARY_CLOUD_NAME;
      const CLOUDINARY_UPLOAD_PRESET = (import.meta as any).env?.VITE_CLOUDINARY_UPLOAD_PRESET;

      if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
        alert('Cloudinary is not configured. Please set VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET in your environment.');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

      const resp = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Background upload failed: ${txt}`);
      }
      const json = await resp.json();
      if (json?.secure_url) {
        setBackgroundUrl(json.secure_url);
      }
    } catch (err) {
      console.error('Background upload error:', err);
      // Keep minimal UI; backgroundUrl remains unchanged on failure
    } finally {
      setIsUploadingBg(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Heuristics to detect free resources if API provides hints; safe fallback shows all if no hints found
  const isFreeAvatar = (a: any) => {
    const hints = [
      a?.is_free === true,
      a?.free === true,
      a?.tier === 'free',
      a?.category === 'free',
      a?.plan === 'free',
      a?.pricing === 'free',
      a?.is_premium === false,
      a?.premium === false,
      a?.price === 0,
    ];
    return hints.some(Boolean);
  };
  const isFreeVoice = (v: any) => {
    const hints = [
      v?.is_free === true,
      v?.free === true,
      v?.tier === 'free',
      v?.category === 'free',
      v?.plan === 'free',
      v?.pricing === 'free',
      v?.is_premium === false,
      v?.premium === false,
      v?.price === 0,
    ];
    return hints.some(Boolean);
  };

  const freeAwareAvatars = useMemo(() => {
    const anyHint = avatars.some(a => isFreeAvatar(a) || (a as any)?.is_premium === true || typeof (a as any)?.price !== 'undefined');
    return anyHint ? avatars.filter(isFreeAvatar) : avatars;
  }, [avatars]);

  const freeAwareVoices = useMemo(() => {
    const anyHint = voices.some(v => isFreeVoice(v) || (v as any)?.is_premium === true || typeof (v as any)?.price !== 'undefined');
    return anyHint ? voices.filter(isFreeVoice) : voices;
  }, [voices]);

  const filteredAvatars = useMemo(() => {
    const q = debouncedAvatarSearch.toLowerCase();
    return freeAwareAvatars.filter(a =>
      (a.avatar_name?.toLowerCase().includes(q) || a.avatar_id?.toLowerCase().includes(q))
    );
  }, [freeAwareAvatars, debouncedAvatarSearch]);

  const filteredVoices = useMemo(() => {
    const q = debouncedVoiceSearch.toLowerCase();
    return freeAwareVoices.filter(v =>
      (v.name?.toLowerCase().includes(q) || v.voice_id?.toLowerCase().includes(q) || v.language?.toLowerCase().includes(q))
    );
  }, [freeAwareVoices, debouncedVoiceSearch]);

  const handleConfirm = () => {
    onSelect(selectedAvatar, selectedVoice, {
      captions_enabled: captionsEnabled,
      background_image_url: backgroundUrl || undefined,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-[100vw] max-h-[100vh] overflow-auto rounded-none px-2 sm:px-6 ${
        isDarkMode ? 'bg-[#1e1e1e] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            🎬 Select Avatar & Voice for Video Generation
          </DialogTitle>
          <DialogDescription className="sr-only">Choose an avatar and voice to generate a video.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-lg">Loading resources...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('avatars')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'avatars'
                    ? 'border-b-2 border-blue-500 text-blue-500'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🧑‍🎤 Avatars ({Math.min(filteredAvatars.length, 24)})
              </button>
              <button
                onClick={() => setActiveTab('voices')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'voices'
                    ? 'border-b-2 border-blue-500 text-blue-500'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🎙️ Voices ({Math.min(filteredVoices.length, 30)})
              </button>
              <button
                onClick={() => setActiveTab('advanced')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'advanced'
                    ? 'border-b-2 border-blue-500 text-blue-500'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                ⚙️ Advanced
              </button>
            </div>

            {/* Avatars Tab */}
            {activeTab === 'avatars' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                  <Input
                    placeholder="Search avatars..."
                    value={avatarSearch}
                    onChange={(e) => setAvatarSearch(e.target.value)}
                    className={`pl-10 ${
                      isDarkMode ? 'bg-[#2e2e2e] border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>

                <div className="flex flex-wrap justify-center gap-3 max-h-96 overflow-y-auto">
                  {filteredAvatars.slice(0, 24).map((avatar) => (
                    <div
                      key={avatar.avatar_id}
                      onClick={() => setSelectedAvatar(avatar)}
                      className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all max-w-[130px] ${
                        selectedAvatar?.avatar_id === avatar.avatar_id
                          ? 'border-blue-500 ring-2 ring-blue-500/50'
                          : isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {avatar.preview_image_url && (
                        <img
                          src={avatar.preview_image_url}
                          alt={avatar.avatar_name}
                          className="w-full h-32 object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className={`p-2 ${isDarkMode ? 'bg-[#2e2e2e]' : 'bg-gray-50'}`}>
                        <div className="font-semibold text-sm truncate">
                          {avatar.avatar_name || avatar.avatar_id}
                        </div>
                        <div className="text-xs opacity-70 truncate">
                          {avatar.gender && `${avatar.gender}`}
                        </div>
                      </div>
                      {selectedAvatar?.avatar_id === avatar.avatar_id && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Voices Tab */}
            {activeTab === 'voices' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                  <Input
                    placeholder="Search voices..."
                    value={voiceSearch}
                    onChange={(e) => setVoiceSearch(e.target.value)}
                    className={`pl-10 ${
                      isDarkMode ? 'bg-[#2e2e2e] border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredVoices.slice(0, 30).map((voice) => (
                    <div
                      key={voice.voice_id}
                      onClick={() => setSelectedVoice(voice)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedVoice?.voice_id === voice.voice_id
                          ? 'border-blue-500 ring-2 ring-blue-500/50'
                          : isDarkMode ? 'border-gray-700 hover:border-gray-600 bg-[#2e2e2e]' : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">
                            {(voice.name || voice.voice_id)}
                          </div>
                          <div className="text-xs opacity-80 flex items-center gap-2 mt-1">
                            {voice.language && <span className="px-2 py-0.5 rounded-full border border-white/20">{voice.language}</span>}
                            {voice.gender && <span className="px-2 py-0.5 rounded-full border border-white/20">{voice.gender}</span>}
                            <span className="truncate opacity-70">ID: {voice.voice_id}</span>
                          </div>
                          {voice.preview_audio && (
                            <div className="mt-2 flex items-center gap-3">
                              <button
                                type="button"
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const id = voice.voice_id;
                                  // pause others
                                  Object.entries(audioRefs.current).forEach(([k, a]) => { if (k !== id && a) a.pause(); });
                                  let ref = audioRefs.current[id];
                                  if (!ref) {
                                    ref = new Audio(voice.preview_audio!);
                                    audioRefs.current[id] = ref;
                                    ref.onended = () => setPlayingVoiceId((cur) => cur === id ? null : cur);
                                  }
                                  if (playingVoiceId === id) {
                                    ref.pause();
                                    setPlayingVoiceId(null);
                                  } else {
                                    ref.currentTime = 0;
                                    ref.play();
                                    setPlayingVoiceId(id);
                                  }
                                }}
                              >
                                <FontAwesomeIcon icon={playingVoiceId === voice.voice_id ? faPause : faPlay} />
                                <span>{playingVoiceId === voice.voice_id ? 'Pause Preview' : 'Play Preview'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                        {selectedVoice?.voice_id === voice.voice_id && (
                          <div className="bg-blue-500 text-white rounded-full p-1 flex-shrink-0">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className={`space-y-3 p-3 rounded-xl ${isDarkMode ? 'bg-[#2e2e2e] border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="text-sm font-semibold">Advanced Options</div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={captionsEnabled} onChange={(e) => setCaptionsEnabled(e.target.checked)} />
                  <span className="flex items-center gap-2"><FontAwesomeIcon icon={faClosedCaptioning} /> Enable Captions</span>
                </label>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faImage} />
                  <Input
                    placeholder="Background image URL (optional)"
                    value={backgroundUrl}
                    onChange={(e) => setBackgroundUrl(e.target.value)}
                    className={isDarkMode ? 'bg-[#1f1f1f] border-gray-700' : ''}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleBackgroundFileChange}
                  />
                  <button
                    type="button"
                    onClick={handlePickBackground}
                    className={`px-3 py-1.5 rounded-md text-sm ${isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}
                    disabled={isUploadingBg}
                  >
                    {isUploadingBg ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
                {backgroundUrl && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={backgroundUrl} alt="Background preview" className="h-16 w-28 object-cover rounded-md border border-white/10" />
                  </div>
                )}
              </div>
            )}

            {/* Selection Summary */}
            {/* <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#2e2e2e]' : 'bg-gray-100'}`}>
              <div className="text-sm font-medium mb-2">Current Selection:</div>
              <div className="space-y-1 text-sm">
                <div>
                  <span className="opacity-70">Avatar:</span>{' '}
                  <span className="font-medium">
                    {selectedAvatar ? selectedAvatar.avatar_name || selectedAvatar.avatar_id : 'None (will use default)'}
                  </span>
                </div>
                <div>
                  <span className="opacity-70">Voice:</span>{' '}
                  <span className="font-medium">
                    {selectedVoice ? selectedVoice.name || selectedVoice.voice_id : 'None (will use default)'}
                  </span>
                </div>
              </div>
            </div> */}

            {/* Actions */}
            <div className="flex gap-3 justify-end bg-transparent">
              <Button
                variant="outline"
                onClick={onClose}
                className={isDarkMode ? 'border-gray-600 hover:bg-gray-800' : ''}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
              >
                Confirm Selection
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
